import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { MoneyIcon } from '@ui/icons/Money.icon'
import { PercentIcon } from '@ui/icons/Percent.icon'
import { FilterChipDropdownList } from '@ui/v2/TableFilter/Chips/FilterChipDropdownList'
import { FilterChipModalDateRange } from '@ui/v2/TableFilter/Chips/FilterChipModalDateRange'
import { FilterChipModalRange } from '@ui/v2/TableFilter/Chips/FilterChipModalRange'
import {
  FilterOptions,
  SearchableEntityProps,
  TableFilterProps,
} from '@ui/v2/TableFilter/TableFilter'

import { debounce } from '@utils/debounce'

import { FilterFields } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import {
  SubscriptionClassificationTypes,
  SubscriptionLevels,
  SubscriptionRequestTypes,
  SubscriptionStatuses,
  SubscriptionTypes,
} from '@model/SubscriptionsDto'

import { CheckBoxProps } from '../../Chips/components/Range.modal'

interface FilterChipByTypeProps extends TableFilterProps {
  filterOption: FilterOptions
}

export const ChipByOption: FC<FilterChipByTypeProps> = (props) => {
  const {
    filterOption,
    customLabels,
    countryOptions = [],
    entityOptions = [],
    currencyOptions = [],
    walletCurrencyOptions = [],
    searchFilters,
    approvalTypes,
    tradingAccountTypeOptions,
    sectionOptions,
    formatOptions,
    rebatePeriods,
    statusOptions,
    tradingAccountSearchProps,
    groupSearchProps,
    campaignSearchProps,
    languagesSearchProps,
    sizesSearchProps,
    categoriesSearchProps,
    clientsSearchProps,
    dateShortcutOptions,
    setSearchFilters,
    showRebateCheckbox,
    types,
    customIcons,
    wallets,
    tradingAccounts,
    transactionStates,
    transactionTypes,
    platformTypes,
    size = 'md',
    tradingAccountOptions,
    openExportModal,
  } = props

  const { t } = useTranslation()

  const dropdown = (
    name: string,
    options?: (NameDto<string | number> | string)[],
    filterType?: FilterOptions,
    more?: Partial<React.ComponentProps<typeof FilterChipDropdownList>>
  ) => (
    <FilterChipDropdownList
      name={name}
      filterType={filterType || filterOption}
      options={options}
      onSelect={(value) => setSearchFilters(value)}
      state={searchFilters}
      size={size}
      {...more}
    />
  )

  const dropdownSearchable = (
    name: string,
    searchProps: SearchableEntityProps | undefined,
    qKey: FilterFields,
    more?: Partial<React.ComponentProps<typeof FilterChipDropdownList>>
  ) =>
    dropdown(name, searchProps?.options, filterOption, {
      hasSearch: true,
      isMultiSelect: true,
      onSearchChange: searchProps?.setSearch
        ? debounce((value) => searchProps.setSearch?.({ [qKey]: value }), 300)
        : undefined,
      ...more,
    })

  const range = (
    name: string,
    keys: { from: FilterFields; to: FilterFields },
    more?: Partial<React.ComponentProps<typeof FilterChipModalRange>>
  ) => (
    <FilterChipModalRange
      name={name}
      onSelect={(value) => setSearchFilters(value)}
      keys={keys}
      state={searchFilters}
      isQueryParameter
      size={size}
      {...more}
    />
  )

  const date = (
    name: string,
    keys: { from: FilterFields; to: FilterFields },
    checkBox?: CheckBoxProps
  ) => (
    <FilterChipModalDateRange
      name={name}
      onSelect={(value) => setSearchFilters(value)}
      keys={keys}
      shortcutOptions={dateShortcutOptions}
      state={searchFilters}
      isQueryParameter
      allowFutureDates={props.allowFutureDates}
      checkBox={checkBox}
      size={size}
      openExportModal={openExportModal}
    />
  )

  switch (filterOption) {
    case FilterOptions.Type:
      return dropdown(customLabels?.[filterOption] || t('Type'), types, filterOption, {
        isMultiSelect: true,
      })

    case FilterOptions.Wallet:
      return dropdown(customLabels?.[filterOption] || t('Wallet.Wallet'), wallets, filterOption, {
        isMultiSelect: true,
        hasSearch: !!(wallets && wallets.length > 6),
      })

    case FilterOptions.SubscriptionType:
      return dropdown(customLabels?.[filterOption] || t('Type'), SubscriptionTypes, filterOption, {
        isMultiSelect: true,
      })

    case FilterOptions.SubscriptionLevel:
      return dropdown(
        customLabels?.[filterOption] || t('Subscriptions.Level'),
        SubscriptionLevels,
        filterOption,
        { isMultiSelect: true }
      )

    case FilterOptions.SubscriptionClassificationType:
      return dropdown(
        customLabels?.[filterOption] || t('Subscriptions.Classification Type'),
        SubscriptionClassificationTypes,
        filterOption,
        { isMultiSelect: true }
      )

    case FilterOptions.SubscriptionStatus:
      return dropdown(
        customLabels?.[filterOption] || t('Status'),
        SubscriptionStatuses,
        filterOption,
        {
          isMultiSelect: true,
        }
      )

    case FilterOptions.SubscriptionRequestType:
      return dropdown(
        customLabels?.[filterOption] || t('Subscriptions.Request Type'),
        SubscriptionRequestTypes,
        filterOption,
        { isMultiSelect: true }
      )

    case FilterOptions.Name:
      return dropdown(customLabels?.[filterOption] || t('Name'), [], filterOption, {
        hasSearch: true,
        isMultiSelect: false,
        onSearchChange: (value) => setSearchFilters({ [FilterOptions.Name]: value }),
      })

    case FilterOptions.TradingAccountPlatformType: {
      const hasMT =
        platformTypes?.some(
          (p) => p.id === PlatformTypeEnum.MT4 || p.id === PlatformTypeEnum.MT5
        ) || false
      const hasTMT = platformTypes?.some((p) => p.id === PlatformTypeEnum.TickmillTrader) || false
      const visible = (tradingAccountSearchProps?.options?.length || 0) > 0 && (hasMT || hasTMT)
      if (!visible) return null
      const options = [
        ...(hasMT ? [{ name: 'MT4/MT5', id: '1,2' }] : []),
        ...(hasTMT ? [{ name: 'Tickmill Trader', id: '4' }] : []),
      ]
      return dropdown(
        customLabels?.[filterOption] || t('Trading Account.Trading Account'),
        options,
        filterOption,
        {
          isMultiSelect: false,
        }
      )
    }

    case FilterOptions.TradingAccount:
      return dropdownSearchable(
        customLabels?.[filterOption] || t('Trading Account.Trading Account'),
        tradingAccountSearchProps,
        FilterFields.TradingAccountName,
        {
          isMultiSelect: true,
          options: tradingAccountSearchProps?.options || tradingAccounts,
        }
      )

    case FilterOptions.RebateApprovalType:
      return dropdown(
        customLabels?.[filterOption] || t('IB.RebatesPage.GroupsPage.Group type'),
        approvalTypes,
        filterOption,
        { isMultiSelect: true }
      )

    case FilterOptions.TradingAccountType:
    case FilterOptions.AccountType:
      return dropdown(
        customLabels?.[filterOption] || t('Trading Account.Account type'),
        tradingAccountTypeOptions,
        filterOption,
        {
          isMultiSelect: true,
          hasSearch: !!(tradingAccountTypeOptions && tradingAccountTypeOptions.length > 1),
        }
      )

    case FilterOptions.Status:
    case FilterOptions.State:
      return dropdown(customLabels?.[filterOption] || t('Status'), statusOptions, filterOption, {
        isMultiSelect: true,
      })

    case FilterOptions.Section:
      return dropdown(
        customLabels?.[filterOption] || t('IB.Export.section'),
        sectionOptions,
        filterOption,
        {
          isMultiSelect: true,
        }
      )
    case FilterOptions.Format:
      return dropdown(
        customLabels?.[filterOption] || t('IB.Export.format'),
        formatOptions,
        filterOption,
        {
          isMultiSelect: true,
        }
      )

    case FilterOptions.Date:
      return date(customLabels?.[filterOption] || t('Date'), {
        from: FilterFields.ClientDateFrom,
        to: FilterFields.ClientDateTo,
      })

    case FilterOptions.TransactionDate:
      return date(customLabels?.[filterOption] || t('Date'), {
        from: FilterFields.TransactionDateFrom,
        to: FilterFields.TransactionDateTo,
      })

    case FilterOptions.StatusChangedAtDate:
      return date(customLabels?.[filterOption] || t('Date'), {
        from: FilterFields.StatusChangedAtDateFrom,
        to: FilterFields.StatusChangedAtDateTo,
      })

    case FilterOptions.CreatedDate:
      return date(customLabels?.[filterOption] || t('Date'), {
        from: FilterFields.CreatedDateFrom,
        to: FilterFields.CreatedDateTo,
      })

    case FilterOptions.RebatePeriod:
      return dropdown(
        customLabels?.[filterOption] || t('IB.RebatesPage.GroupsPage.Rebate period'),
        rebatePeriods,
        filterOption,
        { isMultiSelect: true }
      )

    case FilterOptions.Amount:
      return range(
        customLabels?.[filterOption] || t('Amount'),
        {
          from: FilterFields.AmountFrom,
          to: FilterFields.AmountTo,
        },
        { inputIcon: customIcons?.[filterOption] ?? MoneyIcon }
      )

    case FilterOptions.ClientAmount:
    case FilterOptions.ClientIdAmount:
      return range(
        customLabels?.[filterOption] || t('Amount'),
        {
          from: FilterFields.AmountFrom,
          to: FilterFields.AmountTo,
        },
        { inputIcon: customIcons?.[filterOption] ?? MoneyIcon }
      )
    case FilterOptions.LinkClicked:
      return date(customLabels?.[filterOption] || t('IB.Dashboard.CJM.Link clicked'), {
        from: FilterFields.LinkClickedDateFrom,
        to: FilterFields.LinkClickedDateTo,
      })
    case FilterOptions.RegistrationStarted:
      return date(customLabels?.[filterOption] || t('IB.Dashboard.CJM.Reg. started'), {
        from: FilterFields.ClientRegistrationDateFrom,
        to: FilterFields.ClientRegistrationDateTo,
      })
    case FilterOptions.EmailVerified:
      return date(customLabels?.[filterOption] || t('IB.Dashboard.CJM.emailVerified'), {
        from: FilterFields.EmailVerifiedDateFrom,
        to: FilterFields.EmailVerifiedDateTo,
      })
    case FilterOptions.Activated:
      return date(customLabels?.[filterOption] || t('IB.Dashboard.CJM.activated'), {
        from: FilterFields.ActivatedDateFrom,
        to: FilterFields.ActivatedDateTo,
      })
    case FilterOptions.FirstTimeDeposit:
      return date(customLabels?.[filterOption] || t('IB.Dashboard.CJM.FTD'), {
        from: FilterFields.FirstCompletedDepositDateFrom,
        to: FilterFields.FirstCompletedDepositDateTo,
      })
    case FilterOptions.FirstTimeTrade:
      return date(customLabels?.[filterOption] || t('IB.Dashboard.CJM.FTT'), {
        from: FilterFields.FirstClosedTradeDateFrom,
        to: FilterFields.FirstClosedTradeDateTo,
      })
    case FilterOptions.LastTrade:
      return date(customLabels?.[filterOption] || t('IB.Dashboard.CJM.Last Trade'), {
        from: FilterFields.LastClosedTradeDateFrom,
        to: FilterFields.LastClosedTradeDateTo,
      })

    case FilterOptions.TradingAccountCount:
      return range(customLabels?.[filterOption] || t('IB.RebatesPage.GroupsPage.TAs count'), {
        from: FilterFields.TradingAccountsCountFrom,
        to: FilterFields.TradingAccountsCountTo,
      })

    case FilterOptions.Price:
      return range(
        customLabels?.[filterOption] || t('Subscriptions.Price'),
        {
          from: FilterFields.ClientPriceFrom,
          to: FilterFields.ClientPriceTo,
        },
        { inputIcon: customIcons?.[filterOption] ?? MoneyIcon }
      )

    case FilterOptions.VolumeFxm:
      return range(customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Vol. FXM'), {
        from: FilterFields.VolumeFxmFrom,
        to: FilterFields.VolumeFxmTo,
      })

    case FilterOptions.VolumeOther:
      return range(customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Vol. Other'), {
        from: FilterFields.VolumeOtherFrom,
        to: FilterFields.VolumeOtherTo,
      })

    case FilterOptions.AccumulatedVolumeFxm:
      return range(customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Vol. FXM'), {
        from: FilterFields.AccumulatedVolumeFxmFrom,
        to: FilterFields.AccumulatedVolumeFxmTo,
      })

    case FilterOptions.AccumulatedVolumeOther:
      return range(customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Vol. Other'), {
        from: FilterFields.AccumulatedVolumeOtherFrom,
        to: FilterFields.AccumulatedVolumeOtherTo,
      })

    case FilterOptions.TotalCommission:
      return range(
        customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Commission'),
        {
          from: FilterFields.TotalCommissionFrom,
          to: FilterFields.TotalCommissionTo,
        },
        { inputIcon: customIcons?.[filterOption] ?? MoneyIcon }
      )

    case FilterOptions.AccumulatedCommission:
      return range(
        customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Commission'),
        {
          from: FilterFields.AccumulatedCommissionFrom,
          to: FilterFields.AccumulatedCommissionTo,
        },
        { inputIcon: customIcons?.[filterOption] ?? MoneyIcon }
      )

    case FilterOptions.Commission:
      return range(
        customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Commission'),
        {
          from: FilterFields.QueryAmountFrom,
          to: FilterFields.QueryAmountTo,
        },
        {
          inputIcon: customIcons?.[filterOption] ?? MoneyIcon,
        }
      )

    case FilterOptions.CACommission:
      return range(
        customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Commission'),
        {
          from: FilterFields.AmountFrom,
          to: FilterFields.AmountTo,
        },
        { inputIcon: MoneyIcon }
      )

    case FilterOptions.RebateAmount:
      return range(
        customLabels?.[filterOption] || t('IB.RebatesPage.Rebates'),
        {
          from: FilterFields.RebateAmountFrom,
          to: FilterFields.RebateAmountTo,
        },
        {
          inputIcon: customIcons?.[filterOption] ?? PercentIcon,
          ...(showRebateCheckbox && {
            checkBox: {
              key: FilterFields.HasRebate,
              label: t('IB.RebatesPage.Rebate N/A'),
              checkedValue: 'false',
              uncheckedValue: undefined,
            },
          }),
        }
      )

    case FilterOptions.SignedUpDate:
      return date(customLabels?.[filterOption] || t('IB.RebatesPage.Signed up on'), {
        from: FilterFields.SignedUpDateFrom,
        to: FilterFields.SignedUpDateTo,
      })

    case FilterOptions.RegistrationDate:
      return date(customLabels?.[filterOption] || t('IB.Registration Date'), {
        from: FilterFields.ClientRegistrationDateFrom,
        to: FilterFields.ClientRegistrationDateTo,
      })

    case FilterOptions.TradePeriod:
      return date(customLabels?.[filterOption] || t('IB.Trade Period'), {
        from: FilterFields.ClientDateFrom,
        to: FilterFields.ClientDateTo,
      })

    case FilterOptions.Country:
      return dropdown(
        customLabels?.[filterOption] || t('IB.Country'),
        countryOptions,
        filterOption,
        {
          hasSearch: countryOptions.length > 6,
          isMultiSelect: true,
        }
      )

    case FilterOptions.ClientVolume:
      return range(customLabels?.[filterOption] || t('IB.Volume'), {
        from: FilterFields.QueryVolumeFrom,
        to: FilterFields.QueryVolumeTo,
      })

    case FilterOptions.Volume:
      return range(customLabels?.[filterOption] || t('IB.Volume'), {
        from: FilterFields.ClientVolumeFrom,
        to: FilterFields.ClientVolumeTo,
      })

    case FilterOptions.CAClientVolume:
      return range(customLabels?.[filterOption] || t('IB.Volume'), {
        from: FilterFields.ClientVolumeFrom,
        to: FilterFields.ClientVolumeTo,
      })

    case FilterOptions.IntroducingBrokerGroupId:
    case FilterOptions.RebateGroupId:
    case FilterOptions.IBRebateGroupId:
      return dropdownSearchable(
        customLabels?.[filterOption] || t('IB.RebatesPage.GroupsPage.Group name'),
        groupSearchProps,
        FilterFields.Name,
        { isMultiSelect: true }
      )

    case FilterOptions.TradingAccountId:
      return dropdownSearchable(
        customLabels?.[filterOption] || t('IB.RebatesPage.ClientsPage.Trading account'),
        tradingAccountSearchProps,
        FilterFields.TradingAccountName,
        { isMultiSelect: true }
      )

    case FilterOptions.ClientId:
      return dropdownSearchable(
        customLabels?.[filterOption] || t('IB.Client Name'),
        clientsSearchProps,
        FilterFields.Name,
        { isMultiSelect: true }
      )

    case FilterOptions.Entity:
    case FilterOptions.ClientEntity:
      return dropdown(customLabels?.[filterOption] || t('IB.Entity'), entityOptions, filterOption, {
        hasSearch: !!(entityOptions && entityOptions.length > 1),
        isMultiSelect: true,
      })

    case FilterOptions.Currency:
      return dropdown(
        customLabels?.[filterOption] || t('Currency'),
        currencyOptions,
        filterOption,
        {
          isMultiSelect: true,
        }
      )

    case FilterOptions.WalletCurrencyId:
      return dropdown(
        customLabels?.[filterOption] || t('Currency'),
        walletCurrencyOptions.map(({ id }) => ({ id, name: id })),
        filterOption,
        {
          isMultiSelect: true,
        }
      )

    case FilterOptions.VolumeSince2019:
      return range(customLabels?.[filterOption] || t('IB.Volume Since 2019'), {
        from: FilterFields.VolumeSince2019From,
        to: FilterFields.VolumeSince2019To,
      })

    case FilterOptions.VolumeCurrentMonth:
      return range(customLabels?.[filterOption] || t('IB.Volume Current Month'), {
        from: FilterFields.VolumeCurrentMonthFrom,
        to: FilterFields.VolumeCurrentMonthTo,
      })

    case FilterOptions.VolumePreviousMonth:
      return range(customLabels?.[filterOption] || t('IB.Volume Previous Month'), {
        from: FilterFields.VolumePreviousMonthFrom,
        to: FilterFields.VolumePreviousMonthTo,
      })

    case FilterOptions.Balance:
      return range(customLabels?.[filterOption] || t('Wallet.Balance'), {
        from: FilterFields.BalanceFrom,
        to: FilterFields.BalanceTo,
      })

    case FilterOptions.TransactionType:
      return dropdown(customLabels?.[filterOption] || t('Type'), transactionTypes, filterOption, {
        isMultiSelect: true,
      })

    case FilterOptions.TransactionStatus:
      return dropdown(
        customLabels?.[filterOption] || t('Status'),
        transactionStates,
        filterOption,
        {
          isMultiSelect: true,
        }
      )

    case FilterOptions.CampaignId:
      return dropdownSearchable(
        customLabels?.[filterOption] || t('Trading Account.Campaign'),
        campaignSearchProps,
        FilterFields.CampaignId,
        { isMultiSelect: true }
      )

    case FilterOptions.CategoryId:
      return dropdownSearchable(
        customLabels?.[filterOption] || t('Referral Materials.Category'),
        categoriesSearchProps,
        FilterFields.CategoryId,
        { isMultiSelect: true, hasSearch: false }
      )

    case FilterOptions.LanguageId:
      return dropdownSearchable(
        customLabels?.[filterOption] || t('Language'),
        languagesSearchProps,
        FilterFields.LanguageId,
        { isMultiSelect: true }
      )

    case FilterOptions.SizeId:
      return dropdownSearchable(
        customLabels?.[filterOption] || t('Referral Materials.Size'),
        sizesSearchProps,
        FilterFields.SizeId,
        { isMultiSelect: true }
      )

    case FilterOptions.FxmVolume:
      return range(customLabels?.[filterOption] || t('IB.ReportsPage.Clients.Vol.FXM'), {
        from: FilterFields.FxmVolumeFrom,
        to: FilterFields.FxmVolumeTo,
      })

    case FilterOptions.OtherVolume:
      return range(customLabels?.[filterOption] || t('IB.ReportsPage.Clients.Vol.Other'), {
        from: FilterFields.OtherVolumeFrom,
        to: FilterFields.OtherVolumeTo,
      })

    case FilterOptions.FxmCommission:
      return range(
        customLabels?.[filterOption] || t('IB.ReportsPage.Clients.Commission.FXM'),
        {
          from: FilterFields.FxmAmountFrom,
          to: FilterFields.FxmAmountTo,
        },
        { inputIcon: customIcons?.[filterOption] ?? MoneyIcon }
      )

    case FilterOptions.OtherCommission:
      return range(
        customLabels?.[filterOption] || t('IB.ReportsPage.Clients.Commission.Other'),
        {
          from: FilterFields.OtherAmountFrom,
          to: FilterFields.OtherAmountTo,
        },
        { inputIcon: customIcons?.[filterOption] ?? MoneyIcon }
      )

    case FilterOptions.HasTradingAccounts:
      return dropdown(customLabels?.[filterOption] || t('TA'), tradingAccountOptions, filterOption)

    default:
      return null
  }
}
